import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ProjectList from "../ProjectList";
import { useEffect, useState } from "react";
import { getCompanyCreateProjectData, getLastTenOrderRecords } from "../../Store/Thunk";
import Creatable from "react-select/creatable";
import Select from "react-select";
import validator from "validator";
import {
  ChangeCameraRequirement,
  ChangePreventCheating,
  ChangeProjectSubmitModalIsShown,
  ChangeResultAccess,
  ClearDisabledSelectors,
  ClearIsCandidateAlreadyPresentErrorShown,
  PushToProjectList,
  SetSelectedAssessment,
  SetSelectedOptionByCandidateId,
  SetSelectedPosition,
} from "../../Store";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import ProjectSubmitModal from "../ProjectSubmitModal";
import { reactSelectStyles } from "../../../../SharedComponents/reactSelectStyles";
import IconSvg from "../../../../SharedComponents/IconSvg";
import { UpdateAlertModalData } from "../../../../SharedComponents/GeneralModals/Store";

const CreateProject = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const projectList = useSelector((state) => state.projects.projectList);
  const resultAccess = useSelector((state) => state.projects.resultAccess);
  const preventCheating = useSelector((state) => state.projects.preventCheating);
  const cameraRequirement = useSelector((state) => state.projects.cameraRequirement);
  const reactSelectOptions = useSelector((state) => state.projects.reactSelectOptions);
  const selectedOption = useSelector((state) => state.projects.selectedOption);
  const isCandidateAlreadyPresent = useSelector((state) => state.projects.isCandidateAlreadyPresentErrorShown);

  const [isInvalidEmailSubmitted, setIsInvalidEmailSubmitted] = useState(false);

  useEffect(() => {
    dispatch(getCompanyCreateProjectData());
    dispatch(getLastTenOrderRecords());
  }, []);

  useEffect(() => {
    if (isInvalidEmailSubmitted) {
      dispatch(
        UpdateAlertModalData({
          isShown: true,
          header: "invalidEmailAlert",
          text: "invalidEmailAlertText",
        })
      );
      setIsInvalidEmailSubmitted(false);
    }

    if (isCandidateAlreadyPresent) {
      dispatch(
        UpdateAlertModalData({
          isShown: true,
          header: "candidateIsAlreadyPresentInProjectList",
          text: "projectCreationReAddCandidateForMultiplePackets",
        })
      );
    }
  }, [isInvalidEmailSubmitted, isCandidateAlreadyPresent]);

  const isAnythingDisabled =
    selectedOption.selectedNameOption?.isDisabled ||
    selectedOption.selectedEmailOption?.isDisabled ||
    selectedOption.selectedPositionOption?.isDisabled ||
    selectedOption.selectedAssessmentOption?.isDisabled;

  return (
    <>
      <ProjectSubmitModal />

      <Row className={"mx-0 mt-4  justify-content-between "}>
        <Col xs={12} md={6} xl={true} className={"mb-3 "}>
          <Creatable
            placeholder={t("candidateNamePlaceholder")}
            styles={reactSelectStyles}
            options={reactSelectOptions.nameOptions}
            value={selectedOption.selectedNameOption}
            onChange={(selected) => {
              dispatch(SetSelectedOptionByCandidateId({ ...selected, type: "name" }));
            }}
            isDisabled={selectedOption.selectedNameOption?.isDisabled}
            onBlur={(event) => {
              const inputValue = event.target.value?.trim();
              if (inputValue) {
                const newOption = { value: inputValue, label: inputValue, __isNew__: true };
                dispatch(SetSelectedOptionByCandidateId({ ...newOption, type: "name" }));
              }
            }}
            formatCreateLabel={(inputValue) => `"${inputValue}" ${t("createLabel")}`}
            isValidNewOption={(inputValue) => !!inputValue}
            filterOption={(option, inputValue) => {
              if (!option || !option.label) return false;
              return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
            }}
            getOptionLabel={(option) => (
              <div>
                <Row className={"justify-content-between"}>
                  <Col xs={"auto"}>{option.label}</Col>
                  {option?.blurredText && (
                    <Col xs={"auto"} className={"text-11 text-color-gray-light align-content-center"}>
                      ({option.blurredText})
                    </Col>
                  )}
                </Row>
              </div>
            )}
          />
        </Col>
        <Col xs={12} md={6} xl={true} className={"mb-3"}>
          <Creatable
            placeholder={t("candidateEmailPlaceholder")}
            styles={reactSelectStyles}
            options={reactSelectOptions.emailOptions}
            value={selectedOption.selectedEmailOption}
            onChange={(selected) => {
              if (selected.label?.trim() && !validator.isEmail(selected.label?.trim())) {
                setIsInvalidEmailSubmitted(true);
                return;
              }
              dispatch(SetSelectedOptionByCandidateId({ ...selected, type: "email" }));
            }}
            isDisabled={selectedOption.selectedEmailOption?.isDisabled}
            onBlur={(event) => {
              const inputValue = event.target.value?.trim();
              if (inputValue && validator.isEmail(inputValue)) {
                const newOption = { value: inputValue, label: inputValue, __isNew__: true };
                dispatch(SetSelectedOptionByCandidateId({ ...newOption, type: "email" }));
              } else if (inputValue) {
                setIsInvalidEmailSubmitted(true);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Tab") {
                if (!validator.isEmail(event.target.value)) {
                  event.preventDefault();
                }
              }
            }}
            formatCreateLabel={(inputValue) => `"${inputValue}" ${t("creatingLabel")}`}
          />
        </Col>
        <Col xs={12} md={6} xl={true} className={"mb-3"}>
          <Select
            placeholder={t("positionPlaceholder")}
            styles={reactSelectStyles}
            options={reactSelectOptions.positionOptions}
            value={selectedOption.selectedPositionOption}
            isDisabled={selectedOption.selectedPositionOption?.isDisabled}
            onChange={(selected) => {
              dispatch(SetSelectedPosition(selected));
            }}
          />
        </Col>
        <Col xs={12} md={6} xl={true} className={"mb-3"}>
          <Select
            placeholder={t("assessmentPlaceholder")}
            styles={reactSelectStyles}
            options={reactSelectOptions.assessmentOptions}
            isDisabled={selectedOption.selectedAssessmentOption?.isDisabled}
            value={selectedOption.selectedAssessmentOption?.array}
            onChange={(selected) => {
              dispatch(SetSelectedAssessment(selected));
            }}
            isMulti
          />
        </Col>
        <Col xs={12} xl={"auto"} className={"mb-3"}>
          <Row className={"justify-content-center h100"} style={{ height: "100%" }}>
            <Col xs={6} md={4} xl={"auto"}>
              <Button
                className={"button secondary h-100 w-100"}
                disabled={
                  selectedOption.selectedAssessmentOption?.array?.length === 0 ||
                  !selectedOption.selectedNameOption?.value ||
                  !selectedOption.selectedEmailOption?.value ||
                  !selectedOption.selectedPositionOption?.value
                }
                onClick={() => {
                  dispatch(PushToProjectList());
                }}
              >
                {t("addButtonLabel")}
              </Button>
            </Col>
            {isAnythingDisabled && (
              <Col xs={6} md={4} xl={"auto"}>
                <Button
                  className={"button secondary h-100 w-100"}
                  onClick={() => {
                    dispatch(ClearDisabledSelectors());
                  }}
                >
                  <IconSvg icon={"sweepV2"} fill={"#0077b6"} height={30} width={30}></IconSvg>
                </Button>
              </Col>
            )}
          </Row>
        </Col>
      </Row>

      <ProjectList />

      <div className={"position-absolute "} style={{ bottom: "0", left: 12, right: 12, backgroundColor: "#f8f9fa" }}>
        <Row className={"mx-0 justify-content-end"} style={{ minHeight: "80px" }}>
          <Col xs={"auto"} className={"align-content-center"}>
            <Row>
              <Col>{t("preventCheatingLabel")}</Col>
              <Col xs={"auto"} className={"ps-0"}>
                <Form.Check
                  type={"switch"}
                  onChange={() => dispatch(ChangePreventCheating())}
                  checked={preventCheating}
                />
              </Col>
            </Row>
          </Col>

          <Col xs={"auto"} className={"align-content-center"}>
            <Row>
              <Col>{t("resultAccessLabel")}</Col>
              <Col xs={"auto"} className={"ps-0"}>
                <Form.Check type={"switch"} onChange={() => dispatch(ChangeResultAccess())} checked={resultAccess} />
              </Col>
            </Row>
          </Col>

          <Col xs={"auto"} className={"align-content-center"}>
            <Row>
              <Col>{t("cameraRequirementLabel")}</Col>
              <Col xs={"auto"} className={"ps-0"}>
                <Form.Check
                  type={"switch"}
                  onChange={() => dispatch(ChangeCameraRequirement())}
                  checked={cameraRequirement}
                />
              </Col>
            </Row>
          </Col>

          <Col xs={12} lg={"auto"} className={"align-content-center"}>
            <Button
              className={"button secondary w-100 my-2 my-lg-0"}
              disabled={projectList.length === 0}
              onClick={() => {
                dispatch(ChangeProjectSubmitModalIsShown(true));
              }}
            >
              {t("sendButtonLabel")}
            </Button>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default CreateProject;
